import { useReactiveVar } from '@apollo/client';
import {
  Box,
  Button,
  TextField,
  Grid,
  Container,
  Typography,
  Divider,
  CircularProgress,
  Card,
  Select,
  SelectChangeEvent,
  MenuItem,
  InputLabel,
  FormControl,
  useTheme
} from '@mui/material';
import { Check, CheckCircleOutlined } from '@mui/icons-material';
import { Mark } from 'common/components/Mark';
import { AuthMethod, ProductType } from 'common/types/graphql-types';
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useElements,
  useStripe,
  Elements
} from '@stripe/react-stripe-js';
import * as cardLogos from 'payment/components/Logos';
import { useRouter } from 'next/dist/client/router';
import { selectedProduct } from 'payment/schema/schema';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { StripeTextField } from './StripeTextField';
import {
  loadStripe,
  StripeCardNumberElementChangeEvent
} from '@stripe/stripe-js';
import { FC } from 'react';
import { useCallback } from 'react';
import { StripeLogo } from './StripeLogo';
import { Link as MuiLink } from '@mui/material';
import { Link } from 'common/components/Link';
import { refundRoute, termsRoute } from 'common/routes';
import { usePurchaseProductMutation } from 'payment/mutations/purchaseProduct';
import { trackDetailRoute } from 'track/routes';
import { projectListRoute } from 'project/routes';
import { useMeBillingQuery } from 'user/queries/meBilling';
import { usePreviewInvoiceLazyQuery } from 'payment/queries/previewInvoiceQuery';
import { Fragment } from 'react';
import getConfig from 'next/config';
import { useAnalytics } from 'common/hooks/useAnalytics';
import { CHECKOUT_COMPLETED } from 'payment/events';
import { useAuthenticateMutation } from 'authentication/mutations/authenticate';
import { useMeLazyQuery } from 'user/queries/me';
import { parseCookies } from 'nookies';
import { AUTH_COOKIE_NAME } from 'authentication/schema/schema';
const { publicRuntimeConfig } = getConfig();

interface InputState {
  error?: string;
  complete: boolean;
  value?: string;
}

interface FormState {
  name: InputState;
  coupon: InputState;
  cardNumber: InputState;
  cardExpiry: InputState;
  cardCvc: InputState;
  paymentMethod: InputState;
}

const defaultFormState = {
  name: {
    value: '',
    complete: false
  },
  coupon: {
    value: '',
    complete: true
  },
  cardNumber: {
    complete: false
  },
  cardExpiry: {
    complete: false
  },
  cardCvc: {
    complete: false
  },
  paymentMethod: {
    complete: false,
    value: ''
  }
};

export const WrappedCheckout: FC<CheckoutProps> = ({ onComplete }) => {
  const analytics = useAnalytics();
  const elements = useElements();
  const stripe = useStripe();
  const product = useReactiveVar(selectedProduct);
  const router = useRouter();
  const theme = useTheme();
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [purchaseProduct, { data, loading, error }] =
    usePurchaseProductMutation({ errorPolicy: 'all' });
  const [previewInvoice, { data: invoiceData, loading: invoiceLoading }] =
    usePreviewInvoiceLazyQuery({ fetchPolicy: 'network-only' });
  const [authenticate] = useAuthenticateMutation();
  const [meQuery] = useMeLazyQuery();
  const { data: meData } = useMeBillingQuery();
  const [processing, setProcessing] = useState(false);

  const completed = useMemo(
    () =>
      Object.entries(formState).every(([key, val]) => {
        if (['paymentMethod'].includes(key)) {
          return true;
        }
        return val.complete && !val.error;
      }),
    [formState]
  );

  const handleSubmit = useCallback(async () => {
    const cardElement = elements?.getElement(CardNumberElement);
    if (!stripe || !elements || !cardElement || !product) {
      return;
    }
    let payment = { id: formState.paymentMethod?.value };
    const price = product.prices?.[0];
    setProcessing(true);
    if (!payment?.id) {
      const { error: createPaymentError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: { name: formState.name.value }
        });

      if (createPaymentError || !paymentMethod) {
        setProcessing(false);
        return;
      }
      payment = paymentMethod;
    }
    setProcessing(false);
    const res = await purchaseProduct({
      variables: {
        price: price?.id as string,
        revision: product.revisionId,
        //@ts-ignore
        coupon: formState.coupon.value || window?.Rewardful?.coupon?.id,
        paymentMethod: payment.id as string,
        //@ts-ignore
        referralCode: window?.Rewardful?.referral
      }
    });

    analytics.capture(CHECKOUT_COMPLETED, {
      revision: product.revisionId,
      product: product.id,
      price: price?.id,
      amount: price?.amount,
      interval: price?.interval
    });
    const ReactPixel = await import('react-facebook-pixel').then(
      (x) => x.default
    );
    ReactPixel.track(product.revisionId ? 'Purchase' : 'Subscribe', {
      content_name: product.name,
      value: price?.amount,
      currency: 'USD'
    });
    
    //@ts-ignore
     gtag("event", "purchase", {
        transaction_id: res.data?.purchaseProduct.invoice.id,
        value: price?.amount,
        currency: "USD",
        items: res.data?.purchaseProduct.invoice.items.map((item) => ({
          item_id: item.id,
          item_name: item.description,
          price: item.amount,
          coupon: item.discounts?.map(({ name }) => name).join(','),
          discount: item.discounts?.reduce((acc, {amount}) => {
            acc += amount
            return acc;
          }, 0)
        }))
    });
    if (!!price?.interval) {
      analytics.register({
        subscription: product.id
      });

      const { data: meData } = await meQuery();
      const auth = JSON.parse(parseCookies()[AUTH_COOKIE_NAME] || '{}');
      if (!meData?.me || !auth?.refreshToken) {
        throw new Error('User not authenticated');
      }
      await authenticate({
        variables: {
          identifier: meData.me.id,
          secret: auth.refreshToken,
          authMethod: AuthMethod.RefreshToken
        }
      });
    }
  }, [stripe, formState, elements, product]);

  const handleStripeChange = (event: StripeCardNumberElementChangeEvent) => {
    setFormState({
      ...formState,
      [event.elementType]: {
        complete: event.complete,
        error: event.error?.message
      }
    });
  };

  const handleStateChange = useCallback(
    (
      evt:
        | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      setFormState({
        ...formState,
        [evt.target.name as string]: {
          value: evt.target.value,
          complete: !!evt.target.value
        }
      });
    },
    [formState]
  );

  const handleApplyCoupon = useCallback(() => {
    previewInvoice({
      variables: {
        price: product?.prices?.[0].id as string,
        //@ts-ignore
        coupon: formState.coupon.value || window?.Rewardful?.coupon?.id
      }
    });
  }, [formState]);

  const setApolloError = useCallback(() => {
    if (
      (error?.graphQLErrors?.[0]?.extensions?.errors as any)?.length &&
      !formState.cardNumber?.error
    ) {
      const newState = { ...formState };

      for (const e of error?.graphQLErrors?.[0]?.extensions?.errors as any) {
        let key = `card${e?.property
          ?.charAt(0)
          .toUpperCase()}${e?.property?.slice(1)}` as keyof FormState;
        if (!newState?.[key]) {
          key = 'cardNumber';
        }

        newState[key] = {
          ...formState[key],
          error: e?.message
        };
      }
      setFormState(newState);
    }
  }, [formState, error]);

  useEffect(() => {
    return () => {
      selectedProduct(null);
    };
  }, []);

  useEffect(() => {
    setApolloError();
  }, [error]);

  useEffect(() => {
    if (data?.purchaseProduct?.revision?.master?.wav) {
      window.location.href = data?.purchaseProduct?.revision?.master?.wav;
    }
    if (data?.purchaseProduct && onComplete) {
      onComplete();
    }
  }, [data, onComplete]);

  useEffect(() => {
    if (!product?.prices?.[0]) {
      router.back();
      return;
    }
    previewInvoice({
      variables: {
        price: product?.prices?.[0].id as string,
        //@ts-ignore
        coupon: formState.coupon.value || window?.Rewardful?.coupon?.id
      }
    });
  }, [product]);

  if (!product) {
    return null;
  }

  return (
    <Container maxWidth={'lg'}>
      <Grid
        container
        spacing={10}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Grid item xs={12} sm={5}>
          <Card
            sx={{
              borderRadius: 2,
              padding: theme.spacing(6),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Mark width={100} />
            <Box marginBottom={6} textAlign={'center'}>
              <Typography variant={'h4'}>{product?.name}</Typography>
              {product?.prices?.[0]?.interval && (
                <Typography variant={'body1'} color={'textSecondary'}>
                  {`${product?.prices?.[0]?.interval.toLowerCase()}ly plan`}
                </Typography>
              )}
            </Box>
            {product?.features?.map((feature, i) => (
              <Box
                marginTop={1}
                width={'100%'}
                key={`${feature}-${i}`}
                display={'flex'}
                flexDirection={'row'}
              >
                <Box marginRight={2}>
                  <Check color={'primary'} fontSize={'small'} />
                </Box>
                <Typography variant={'caption'} color={'textSecondary'}>
                  {feature}
                </Typography>
              </Box>
            ))}
          </Card>
          <Box marginTop={4}>
            <Grid container alignItems={'center'} spacing={4}>
              <Grid item>
                <MuiLink
                  sx={{
                    display: 'flex',
                    color: theme.palette.divider
                  }}
                  href={'https://stripe.com'}
                  target={'_blank'}
                >
                  <StripeLogo />
                </MuiLink>
              </Grid>
              <Grid item>
                <Box height={40}>
                  <Divider orientation={'vertical'} />
                </Box>
              </Grid>
              <Grid item>
                <Link
                  sx={{
                    display: 'flex',
                    color: theme.palette.divider
                  }}
                  variant={'body2'}
                  color={'textSecondary'}
                  href={termsRoute()}
                >
                  Terms
                </Link>
              </Grid>
              <Grid item>
                <Link
                  sx={{
                    display: 'flex',
                    color: theme.palette.divider
                  }}
                  variant={'body2'}
                  color={'textSecondary'}
                  href={refundRoute()}
                >
                  Refund Policy
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} sm={5}>
          {!data || onComplete ? (
            <>
              {product.type === ProductType.Recurring && (
                <Box marginBottom={4}>
                  <Typography variant={'h6'}>Billing cycle</Typography>
                </Box>
              )}
              <Box
                borderRadius='4px'
                padding={2}
                border={1}
                borderColor={'primary.dark'}
                display={'flex'}
                alignItems={'center'}
                flexDirection={'row'}
              >
                <Check fontSize={'large'} color={'primary'} />
                <Box marginLeft={2}>
                  <Typography variant={'h4'}>
                    ${product.prices?.[0].amount.toFixed(2)}
                  </Typography>
                  <Typography variant={'body1'} color={'textSecondary'}>
                    {product.type === ProductType.Recurring &&
                    product.prices?.[0].interval
                      ? `Billed ${product.prices?.[0].interval.toLowerCase()}ly`
                      : 'Due today'}
                  </Typography>
                </Box>
              </Box>
              <Box marginTop={4} marginBottom={3}>
                <Typography variant={'h6'}>Payment information</Typography>
              </Box>
              <Grid container spacing={3}>
                {!!meData?.me?.cards?.length && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Select existing card</InputLabel>
                      <Select
                        name={'paymentMethod'}
                        value={formState.paymentMethod?.value}
                        onChange={handleStateChange}
                      >
                        <MenuItem value={''}>None</MenuItem>
                        {meData?.me.cards.map(
                          ({ id, brand, last4, expiration }) => {
                            const cardLogo = Object.entries(
                              cardLogos
                            ).reduce<JSX.Element | null>((acc, [key, Logo]) => {
                              if (key.toUpperCase() !== brand) {
                                return acc;
                              }
                              return <Logo width={40} />;
                            }, null);
                            return (
                              <MenuItem value={id} key={id}>
                                <Box display={'flex'}>
                                  {cardLogo}
                                  <Box marginLeft={2}>
                                    <Typography
                                      component={'p'}
                                      variant={'caption'}
                                    >{`Card ending in ${last4}`}</Typography>
                                    <Typography
                                      component={'p'}
                                      variant={'caption'}
                                    >{`exp ${expiration}`}</Typography>
                                  </Box>
                                </Box>
                              </MenuItem>
                            );
                          }
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    disabled={!!formState.paymentMethod.value}
                    name={'name'}
                    label='Name on card'
                    onChange={handleStateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StripeTextField
                    onChange={handleStripeChange}
                    label='Card number'
                    error={!!formState.cardNumber.error}
                    disabled={!!formState.paymentMethod.value}
                    helperText={formState.cardNumber.error}
                    stripeElement={CardNumberElement}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StripeTextField
                    onChange={handleStripeChange}
                    label='Exp. date'
                    error={!!formState.cardExpiry.error}
                    disabled={!!formState.paymentMethod.value}
                    helperText={formState.cardExpiry.error}
                    stripeElement={CardExpiryElement}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StripeTextField
                    onChange={handleStripeChange}
                    label='CVC'
                    error={!!formState.cardCvc.error}
                    disabled={!!formState.paymentMethod.value}
                    helperText={formState.cardCvc.error}
                    stripeElement={CardCvcElement}
                  />
                </Grid>
              </Grid>
              <Box marginTop={4} marginBottom={3}>
                <Typography variant={'h6'}>Have a discount code?</Typography>
              </Box>
              <Grid container spacing={3} alignItems={'center'}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    name={'coupon'}
                    //@ts-ignore
                    disabled={!!window?.Rewardful?.coupon?.id}
                    placeholder='Enter discount code here'
                    onChange={handleStateChange}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button
                    disabled={!formState.coupon.value || invoiceLoading}
                    fullWidth
                    variant={'outlined'}
                    onClick={handleApplyCoupon}
                  >
                    Apply
                  </Button>
                </Grid>
              </Grid>
              <Box marginTop={4} marginBottom={4}>
                <Divider />
              </Box>
              {invoiceLoading ? (
                <Box display={'flex'} justifyContent={'center'}>
                  <CircularProgress size={50} />
                </Box>
              ) : (
                <>
                  {invoiceData?.previewInvoice.items.map((item) => {
                    return (
                      <Fragment key={item.id}>
                        <Box
                          display={'flex'}
                          marginBottom={0.5}
                          flexDirection={'row'}
                          justifyContent={'space-between'}
                        >
                          <Typography
                            variant={'caption'}
                            color={'textSecondary'}
                          >
                            {item.description}
                          </Typography>
                          <Typography
                            variant={'caption'}
                            color={'textSecondary'}
                          >
                            {item.amount > 0
                              ? `$${item.amount.toFixed(2)}`
                              : `-$${(item.amount * -1).toFixed(2)}`}
                          </Typography>
                        </Box>
                        {item.discounts?.map((discount) => {
                          return (
                            <Box
                              key={discount.name}
                              display={'flex'}
                              marginBottom={0.5}
                              flexDirection={'row'}
                              justifyContent={'space-between'}
                            >
                              <Typography
                                variant={'caption'}
                                color={'textSecondary'}
                              >
                                {discount.name}
                              </Typography>
                              <Typography
                                variant={'caption'}
                                color={'textSecondary'}
                              >
                                -${discount.amount.toFixed(2)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Fragment>
                    );
                  })}

                  <Box
                    marginTop={2}
                    marginBottom={5}
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'space-between'}
                  >
                    <Typography variant={'body2'}>Amount due</Typography>
                    <Typography variant={'body2'}>
                      ${invoiceData?.previewInvoice?.amount.toFixed(2)}
                    </Typography>
                  </Box>
                  <Button
                    fullWidth
                    variant={'contained'}
                    color={'primary'}
                    disabled={
                      !stripe ||
                      processing ||
                      loading ||
                      (!completed && !formState.paymentMethod.complete)
                    }
                    onClick={handleSubmit}
                  >
                    {processing || loading ? (
                      <CircularProgress />
                    ) : (
                      `Pay $${invoiceData?.previewInvoice.amount.toFixed(2)}`
                    )}
                  </Button>
                </>
              )}
            </>
          ) : (
            <Box
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={'center'}
            >
              <CheckCircleOutlined
                color={'primary'}
                sx={{
                  fontSize: theme.spacing(13),
                  marginBottom: theme.spacing(5)
                }}
              />
              <Typography variant={'h4'}>Congrats!</Typography>
              {data?.purchaseProduct.user ? (
                <>
                  <Box maxWidth={'40%'} margin={2} marginBottom={8}>
                    <Typography variant={'h5'} align={'center'}>
                      Your account has been upgraded
                    </Typography>
                  </Box>
                  <Link sx={{ width: '100%' }} href={projectListRoute()}>
                    <Button fullWidth variant={'contained'} color={'primary'}>
                      Go to my projects
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Box maxWidth={'50%'} margin={2} marginBottom={8}>
                    <Typography variant={'h5'} align={'center'}>
                      Your purchase was successful
                    </Typography>
                  </Box>
                  <Link
                    href={trackDetailRoute(
                      data!.purchaseProduct!.revision!.track.id,
                      data!.purchaseProduct!.revision!.id
                    )}
                    sx={{
                      width: '100%'
                    }}
                  >
                    <Button fullWidth color={'primary'} variant={'contained'}>
                      Return to track
                    </Button>
                  </Link>
                </>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

interface CheckoutProps {
  onComplete?: () => void;
}

export const Checkout: FC<CheckoutProps> = (props) => {
  const stripePromise = useMemo(() => {
    if (publicRuntimeConfig.stripeApiKey) {
      return loadStripe(publicRuntimeConfig.stripeApiKey);
    }
    throw new Error('No Stripe Api key');
  }, []);

  return (
    <Elements
      stripe={stripePromise}
      options={{
        fonts: [
          {
            cssSrc:
              'https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700&display=swap'
          }
        ]
      }}
    >
      <WrappedCheckout {...props} />
    </Elements>
  );
};
